import { useCallback } from "react";
import { postWithRetry } from "../../api/axios";
import { FlowState, JobStatus } from "./types";
import { ExtractedContract } from "../../types/contract";
import { RentRollItem, ContractSaveError, SessionConfirmedData } from "../../types/contract";

export const useFinalDataSave = (
  finalContracts: ExtractedContract[],
  rentRollData: RentRollItem[],
  jobStatuses: Record<string, JobStatus>,
  setSnackbar: (snackbar: { open: boolean; message: string; severity?: "success" | "info" | "warning" | "error" }) => void,
  setLoading: (loading: boolean) => void,
  setFlowState: (state: FlowState) => void,
  setError: (error: string | null) => void,
  setFailedContractsDetails: (details: ContractSaveError[]) => void,
  setShowFailedContractsDialog: (show: boolean) => void,
  forceRefresh: () => void,
  setChangedContractIds: (ids: Set<number>) => void,
  setSessionConfirmedData: (data: SessionConfirmedData) => void,
  setDbCheckedContracts: (contracts: Set<number>) => void
) => {
  const handleSaveFinalData = useCallback(async () => {
    // Always send all extracted contracts to the backend for the final save and deduplication pass.
    if (finalContracts.length === 0 && rentRollData.length === 0) {
      setSnackbar({ open: true, message: "保存するデータがありません。" });
      setFlowState("saved");
      return;
    }

    setLoading(true);
    setFlowState("saving");
    setError(null);
    try {
      const job_ids = Object.keys(jobStatuses);
      const response = await postWithRetry(`/api/save-final-data/`, {
        contracts: finalContracts,
        rent_roll_items: rentRollData,
        job_ids: job_ids,
      });

      // レスポンスの処理（部分保存の対応）
      if (response.status === "success") {
        setFlowState("saved");
      } else if (response.status === "partial_success") {
        setFlowState("saved");
        // 失敗した契約の詳細を保存
        setFailedContractsDetails(response.failed_contracts);
        setShowFailedContractsDialog(true);
        setError(`${response.failed_count}件の契約の保存に失敗しました。詳細を確認してください。`);
      } else {
        setFlowState("error");
        // 失敗した契約の詳細を保存
        setFailedContractsDetails(response.failed_contracts);
        setShowFailedContractsDialog(true);
        setError(`すべての契約の保存に失敗しました。`);
      }
      forceRefresh(); // Force a refresh of the global data
      setChangedContractIds(new Set()); // Clear changes after successful save

      // sessionConfirmedData をクリア
      setSessionConfirmedData({ buildings: [] });

      // DB照合済みフラグをクリア
      setDbCheckedContracts(new Set());

      // setSnackbar({
      //   open: true,
      //   message: "データベースへの保存が完了しました。",
      // });
    } catch (err) {
      setError("データベースへの保存中にエラーが発生しました。");
      setFlowState("error");
    } finally {
      setLoading(false);
    }
  }, [
    finalContracts,
    rentRollData,
    jobStatuses,
    setSnackbar,
    setLoading,
    setFlowState,
    setError,
    setFailedContractsDetails,
    setShowFailedContractsDialog,
    forceRefresh,
    setChangedContractIds,
    setSessionConfirmedData,
    setDbCheckedContracts,
  ]);

  return {
    handleSaveFinalData,
  };
};